/**
 * MaterialsPanel - the "Fabrication" tab (registered as panels.materials).
 *
 * Two sub-tabs (segmented control at the top of the panel):
 *  - Cut: working material settings (sheet width/height and part gap feed
 *    the paged packer; material thickness rewrites every plane's thickness
 *    and regenerates), Save SVG/DXF. While this sub-tab is active the main
 *    viewport becomes a split screen: the 3D view keeps the left half and
 *    a white overlay on the right half shows the cut-pattern preview one
 *    sheet page at a time, with a small pager pill at its top.
 *  - 3D Print: scale (%) input, model dimensions readout (mm), binary
 *    STL export of the generated meshes.
 *
 * The preview refreshes debounced on shape changes (Application's
 * onShapeChange callback calls refreshPattern()), immediately on
 * material-setting changes, and whenever the tab/sub-tab becomes visible.
 * The panel is mounted into the right properties panel while Make mode is
 * on (UIManager.setMode); UIManager hides the split-screen overlay via
 * setSplitScreen(false) when Make mode exits. The overlay leaves a
 * 296px gutter on the right so the pattern is never covered by the
 * properties panel (16px margin + 264px panel + 16px margin).
 */

import * as THREE from 'three';
import { collectParts, buildSVG } from '../../export/SVGExporter.js';
import { buildSTL, computeMeshesBBox, buildFlatPartMeshes } from '../../export/STLExporter.js';

const REFRESH_DEBOUNCE_MS = 800;

// Sheet-size presets (mm). sheetWidth is the fixed packing width, sheetHeight
// the page height; `scale` is the cut-pattern scale (%) auto-applied when the
// preset is chosen - full sheets cut at real size (100%), while small laser
// beds and paper shrink the pattern so a scaled model fits. The first entry
// is the app default (4x8 ft). `group` drives the <optgroup> headings.
const SHEET_PRESETS = [
    // Sheet material (full size)
    { group: 'Sheet material', label: '4×8 ft — plywood/MDF/OSB (1219×2438)', w: 1219, h: 2438, scale: 100 },
    { group: 'Sheet material', label: '4×4 ft — half sheet (1219×1219)', w: 1219, h: 1219, scale: 100 },
    { group: 'Sheet material', label: '2×4 ft — quarter sheet (610×1219)', w: 610, h: 1219, scale: 100 },
    { group: 'Sheet material', label: '5×5 ft — Baltic birch (1524×1524)', w: 1524, h: 1524, scale: 100 },
    { group: 'Sheet material', label: '2×2 ft — hobby/CNC (610×610)', w: 610, h: 610, scale: 100 },
    // Laser cutter beds (landscape)
    { group: 'Laser cutter', label: 'Glowforge (495×279)', w: 495, h: 279, scale: 50 },
    { group: 'Laser cutter', label: 'K40 (300×200)', w: 300, h: 200, scale: 40 },
    { group: 'Laser cutter', label: 'Epilog 24×18 in (610×457)', w: 610, h: 457, scale: 60 },
    { group: 'Laser cutter', label: 'Generic 600×400', w: 600, h: 400, scale: 60 },
    { group: 'Laser cutter', label: 'Large 900×600', w: 900, h: 600, scale: 80 },
    // Paper (portrait)
    { group: 'Paper', label: 'A4 (210×297)', w: 210, h: 297, scale: 15 },
    { group: 'Paper', label: 'A3 (297×420)', w: 297, h: 420, scale: 20 },
    { group: 'Paper', label: 'US Letter (216×279)', w: 216, h: 279, scale: 15 },
    { group: 'Paper', label: 'US Tabloid (279×432)', w: 279, h: 432, scale: 20 }
];

// Assembled <-> flat-parts animation (3D Print "Unassembled" mode)
const FLAT_ANIM_MS = 900;      // per-part tween duration
const FLAT_STAGGER_MS = 15;    // extra delay per part index

// Scene rotation while the flat preview is up: looking DOWN at the parts
// from above, like a print bed (positive rotationX looks from above)
const FLAT_VIEW_ROTATION = { x: 1.1, y: 0 };

function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export class MaterialsPanel {
    constructor(application) {
        this.app = application;

        // Shared fabrication settings live on the app so DesignIO's
        // exports (e.g. Ctrl+E) use the same packing options
        if (!this.app.fabSettings) {
            // Standard US sheet material: 4x8 ft (48"x96") = 1219x2438 mm
            this.app.fabSettings = { sheetWidth: 1219, sheetHeight: 2438, gap: 10, cutScalePct: 100 };
        }
        if (typeof this.app.fabSettings.sheetHeight !== 'number') {
            this.app.fabSettings.sheetHeight = 2438;
        }
        if (typeof this.app.fabSettings.cutScalePct !== 'number') {
            this.app.fabSettings.cutScalePct = 100;
        }
        if (typeof this.app.materialThickness !== 'number') {
            this.app.materialThickness = 5.0;
        }

        this.scalePct = 100; // 3D print scale: 100% = real size (1px = 1mm)
        // Print mode: 'assembled' (with slots, as generated),
        // 'noslots' (assembled, slot cutouts skipped),
        // 'flat' (unassembled parts packed flat on the ground plane)
        this.printMode = 'assembled';
        this.activeSubTab = 'cut';
        this.previewPage = 0; // 0-based sheet page shown in the split view
        this._refreshTimer = null;

        // Overlay first: createPanel's initial setSubTab already runs the
        // refresh path, which shows/hides the overlay
        this.createSplitOverlay();
        this.createPanel();
    }

    /**
     * Current ShapePacker options for preview + exports
     */
    getPackOpts() {
        return {
            sheetWidth: this.app.fabSettings.sheetWidth,
            sheetHeight: this.app.fabSettings.sheetHeight,
            gap: this.app.fabSettings.gap,
            scale: (this.app.fabSettings.cutScalePct ?? 100) / 100
        };
    }

    /**
     * Create the panel DOM structure
     */
    createPanel() {
        const panel = document.createElement('div');
        panel.className = 'materials-panel panel';
        panel.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 10px;
            width: 100%;
        `;

        // --- Segmented sub-tab control ("Cut" | "3D Print") ---
        const subTabBar = document.createElement('div');
        subTabBar.style.cssText = `
            display: inline-flex;
            align-self: flex-start;
            background: rgba(0, 0, 0, 0.08);
            border-radius: 8px;
            padding: 3px;
            gap: 2px;
        `;
        this.subTabButtons = {
            cut: this.createSubTabButton('cut', 'Cut'),
            print: this.createSubTabButton('print', '3D Print')
        };
        subTabBar.appendChild(this.subTabButtons.cut);
        subTabBar.appendChild(this.subTabButtons.print);
        panel.appendChild(subTabBar);

        // --- Sub-tab contents ---
        this.cutContent = this.createCutContent();
        this.printContent = this.createPrintContent();
        panel.appendChild(this.cutContent);
        panel.appendChild(this.printContent);

        this.panel = panel;
        this.setSubTab('cut');
    }

    /**
     * Create one segment of the Cut / 3D Print segmented control
     */
    createSubTabButton(id, label) {
        const button = document.createElement('button');
        button.textContent = label;
        button.style.cssText = `
            border: none;
            border-radius: 6px;
            padding: 6px 22px;
            cursor: pointer;
            font-size: 13px;
            font-weight: 600;
            background: transparent;
            color: #555;
            transition: background 0.15s, color 0.15s, box-shadow 0.15s;
        `;
        button.addEventListener('click', () => this.setSubTab(id));
        return button;
    }

    /**
     * Switch between the Cut and 3D Print sub-tabs
     */
    setSubTab(id) {
        this.activeSubTab = id;
        queueMicrotask(() => this.updatePrintPreview());

        this.cutContent.style.display = id === 'cut' ? 'flex' : 'none';
        this.printContent.style.display = id === 'print' ? 'flex' : 'none';

        Object.entries(this.subTabButtons).forEach(([key, button]) => {
            if (key === id) {
                button.style.background = 'var(--accent)';
                button.style.color = '#fff';
                button.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.25)';
            } else {
                button.style.background = 'transparent';
                button.style.color = '#555';
                button.style.boxShadow = 'none';
            }
        });

        // Refresh immediately when a sub-tab becomes visible (also shows/
        // hides the split-screen preview overlay)
        this.refreshNow();
    }

    /**
     * Cut sub-tab: material settings + SVG/DXF export (the pattern preview
     * itself lives in the split-screen overlay on the main viewport)
     */
    createCutContent() {
        const div = document.createElement('div');
        div.style.cssText = `
            display: flex;
            gap: 24px;
            align-items: flex-start;
            flex-wrap: wrap;
        `;

        // Material settings (these actually apply)
        const settings = document.createElement('div');
        settings.style.cssText = 'display: flex; flex-direction: column; gap: 8px;';

        const title = document.createElement('div');
        title.textContent = 'Material';
        title.style.cssText = `
            color: var(--text-dim);
            font-size: 10px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.8px;
        `;
        settings.appendChild(title);

        // Preset dropdown: picks a standard sheet size, fills the width/
        // height fields AND auto-sets the cut scale (small laser beds/paper
        // shrink the pattern to fit). Manually editing width/height/scale
        // flips it to "Custom" (see matchSheetPreset / the field callbacks).
        const presetSelect = this.createSelectControl(
            'Sheet size',
            [...SHEET_PRESETS.map((p, i) => ({ group: p.group, label: p.label, value: String(i) })),
             { label: 'Custom', value: 'custom' }],
            this.matchSheetPreset(),
            (val) => {
                if (val === 'custom') return;
                const p = SHEET_PRESETS[Number(val)];
                if (!p) return;
                this.app.fabSettings.sheetWidth = p.w;
                this.app.fabSettings.sheetHeight = p.h;
                this.app.fabSettings.cutScalePct = p.scale;
                widthRow.querySelector('input').value = p.w;
                heightRow.querySelector('input').value = p.h;
                scaleRow.querySelector('input').value = p.scale;
                this.refreshNow();
            }
        );
        settings.appendChild(presetSelect);

        const syncPresetToCustom = () => {
            presetSelect.querySelector('select').value = this.matchSheetPreset();
        };

        const widthRow = this.createNumberControl(
            'Sheet width', this.app.fabSettings.sheetWidth, 'mm',
            { min: 50, step: 10 },
            (v) => {
                this.app.fabSettings.sheetWidth = v;
                syncPresetToCustom();
                this.refreshNow();
            }
        );
        settings.appendChild(widthRow);
        const heightRow = this.createNumberControl(
            'Sheet height', this.app.fabSettings.sheetHeight, 'mm',
            { min: 50, step: 10 },
            (v) => {
                this.app.fabSettings.sheetHeight = v;
                syncPresetToCustom();
                this.refreshNow();
            }
        );
        settings.appendChild(heightRow);
        const scaleRow = this.createNumberControl(
            'Scale', this.app.fabSettings.cutScalePct ?? 100, '%',
            { min: 1, step: 1 },
            (v) => {
                this.app.fabSettings.cutScalePct = v;
                syncPresetToCustom();
                this.refreshNow();
            }
        );
        settings.appendChild(scaleRow);
        settings.appendChild(this.createNumberControl(
            'Part gap', this.app.fabSettings.gap, 'mm',
            { min: 0, step: 1 },
            (v) => {
                this.app.fabSettings.gap = v;
                this.refreshNow();
            }
        ));
        // FABRICATION-ONLY thickness override. The DESIGN thickness lives
        // in the Layers section (app.setMaterialThickness); this override,
        // when > 0, takes precedence for planes/slot widths/exports so the
        // cut pattern can target a different stock without touching the
        // design value. 0 clears the override.
        settings.appendChild(this.createNumberControl(
            'Thickness override', this.app.fabSettings.thicknessOverride ?? 0, 'mm',
            { min: 0, step: 0.5 },
            (v) => {
                this.app.setFabThicknessOverride(v > 0 ? v : null);
                // Regeneration is async - the debounced refresh picks up
                // the regenerated slots
                this.refreshPattern();
            }
        ));

        div.appendChild(settings);

        // Export buttons
        const exports = document.createElement('div');
        exports.style.cssText = 'display: flex; flex-direction: column; gap: 8px;';

        const exportTitle = document.createElement('div');
        exportTitle.textContent = 'Export';
        exportTitle.style.cssText = title.style.cssText;
        exports.appendChild(exportTitle);

        exports.appendChild(this.createActionButton('Save SVG',
            'Export cut pattern as SVG, one file per sheet page (Ctrl+E)',
            () => this.app.exportSVG(true, this.getPackOpts())));
        exports.appendChild(this.createActionButton('Save DXF',
            'Export cut pattern as DXF (CNC/laser), one file per sheet page',
            () => this.app.exportDXF(true, this.getPackOpts())));

        div.appendChild(exports);

        return div;
    }

    /**
     * 3D Print sub-tab: scale, dimensions readout, STL export
     */
    createPrintContent() {
        const div = document.createElement('div');
        div.style.cssText = `
            display: flex;
            gap: 24px;
            align-items: flex-start;
            flex-wrap: wrap;
        `;

        const settings = document.createElement('div');
        settings.style.cssText = 'display: flex; flex-direction: column; gap: 8px;';

        const title = document.createElement('div');
        title.textContent = '3D Print';
        title.style.cssText = `
            color: var(--text-dim);
            font-size: 10px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.8px;
        `;
        settings.appendChild(title);

        // Print mode radio group
        settings.appendChild(this.createPrintModeControl());

        settings.appendChild(this.createNumberControl(
            'Scale', this.scalePct, '%',
            { min: 1, step: 1 },
            (v) => {
                this.scalePct = v;
                this.updateDimensions();
            }
        ));

        this.dimsInfo = document.createElement('div');
        this.dimsInfo.style.cssText = 'color: #555; font-size: 12px;';
        this.dimsInfo.textContent = 'Model: -';
        settings.appendChild(this.dimsInfo);

        const hint = document.createElement('div');
        hint.textContent = '100% = real size (1 sketch px = 1 mm)';
        hint.style.cssText = 'color: #999; font-size: 11px;';
        settings.appendChild(hint);

        div.appendChild(settings);

        const exports = document.createElement('div');
        exports.style.cssText = 'display: flex; flex-direction: column; gap: 8px;';

        const exportTitle = document.createElement('div');
        exportTitle.textContent = 'Export';
        exportTitle.style.cssText = title.style.cssText;
        exports.appendChild(exportTitle);
        exports.appendChild(this.createActionButton('Export STL',
            'Export the 3D model as a binary STL (mm)',
            () => this.exportSTL()));

        div.appendChild(exports);

        return div;
    }

    /**
     * Radio group for the 3D print mode (assembled with/without slots,
     * or unassembled flat-packed parts)
     */
    createPrintModeControl() {
        const wrap = document.createElement('div');
        wrap.style.cssText = 'display: flex; flex-direction: column; gap: 4px;';

        const label = document.createElement('div');
        label.textContent = 'Print mode';
        label.style.cssText = 'color: #555; font-size: 12px;';
        wrap.appendChild(label);

        const modes = [
            ['assembled', 'Assembled (with slots)'],
            ['noslots', 'Assembled (no slots)'],
            ['flat', 'Unassembled (flat parts)']
        ];

        for (const [value, text] of modes) {
            const row = document.createElement('label');
            row.style.cssText = `
                display: flex;
                align-items: center;
                gap: 6px;
                color: #333;
                font-size: 12px;
                cursor: pointer;
            `;

            const radio = document.createElement('input');
            radio.type = 'radio';
            radio.name = 'print-mode';
            radio.value = value;
            radio.checked = this.printMode === value;
            radio.style.cssText = 'margin: 0; cursor: pointer;';
            radio.addEventListener('change', () => {
                if (radio.checked) {
                    this.printMode = value;
                    this.updateDimensions();
                    this.updatePrintPreview();
                }
            });

            const span = document.createElement('span');
            span.textContent = text;

            row.appendChild(radio);
            row.appendChild(span);
            wrap.appendChild(row);
        }

        return wrap;
    }

    // =========================================================================
    // Split-screen cut-pattern preview (overlay on the main viewport)
    // =========================================================================

    /**
     * Build the right-half overlay that turns the main viewport into a
     * split screen: 3D view left, cut-pattern preview right. The overlay
     * simply covers the right half of the canvas (which keeps rendering
     * full width underneath), so no camera/canvas resizing is needed and
     * window resizes are handled purely by CSS percentages.
     */
    createSplitOverlay() {
        const host = document.getElementById('canvas-container') || document.body;

        // The overlay's left edge splits the viewport in half; its right
        // edge stops short of the right properties panel (which hosts the
        // Make settings) so the pattern is never covered by it.
        const overlay = document.createElement('div');
        overlay.id = 'cut-pattern-overlay';
        overlay.style.cssText = `
            position: absolute;
            top: 0;
            left: 50%;
            right: 296px;
            bottom: 0;
            background: #ffffff;
            border-left: 1px solid #bbb;
            display: none;
            z-index: 10;
        `;

        // Pattern host (inline SVG scaled to fit, aspect preserved)
        this.previewDiv = document.createElement('div');
        this.previewDiv.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            padding: 64px 16px 16px;
            box-sizing: border-box;
        `;
        overlay.appendChild(this.previewDiv);

        // Floating pager pill at the top of the preview
        const pager = document.createElement('div');
        pager.style.cssText = `
            position: absolute;
            top: 10px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            background: rgba(255, 255, 255, 0.92);
            border: 1px solid #ddd;
            border-radius: 14px;
            padding: 4px 14px;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
            z-index: 2;
        `;

        const pagerRow = document.createElement('div');
        pagerRow.style.cssText = 'display: flex; align-items: center; gap: 10px;';

        this.prevPageBtn = this.createPagerButton('‹',
            () => this.setPreviewPage(this.previewPage - 1));
        this.nextPageBtn = this.createPagerButton('›',
            () => this.setPreviewPage(this.previewPage + 1));

        this.pageLabel = document.createElement('div');
        this.pageLabel.textContent = 'Page 1/1';
        this.pageLabel.style.cssText = 'color: #333; font-size: 12px; font-weight: 600;';

        pagerRow.appendChild(this.prevPageBtn);
        pagerRow.appendChild(this.pageLabel);
        pagerRow.appendChild(this.nextPageBtn);
        pager.appendChild(pagerRow);

        this.previewInfo = document.createElement('div');
        this.previewInfo.style.cssText = 'color: #777; font-size: 11px; white-space: nowrap;';
        pager.appendChild(this.previewInfo);

        overlay.appendChild(pager);
        host.appendChild(overlay);
        this.overlay = overlay;
    }

    /**
     * Small round pager button
     */
    createPagerButton(glyph, action) {
        const button = document.createElement('button');
        button.textContent = glyph;
        button.style.cssText = `
            border: none;
            background: transparent;
            color: #333;
            font-size: 18px;
            line-height: 1;
            padding: 0 6px;
            cursor: pointer;
        `;
        button.addEventListener('click', action);
        return button;
    }

    /**
     * Jump the preview to another sheet page (lower-clamped here; the
     * upper clamp comes back from buildSVG's page echo in refreshNow)
     */
    setPreviewPage(page) {
        this.previewPage = Math.max(0, page);
        this.refreshNow();
    }

    /**
     * Show/hide the overlay based on current state: visible only while the
     * Fabrication tab is in the DOM AND the Cut sub-tab is selected.
     * @returns {boolean} Whether the overlay is now visible
     */
    updateOverlayVisibility() {
        const visible = this.activeSubTab === 'cut' && !!(this.panel && this.panel.isConnected);
        this.overlay.style.display = visible ? 'block' : 'none';
        if (visible) this.fitPreviewAboveUI();
        this.applySplitCameraShift(visible);
        return visible;
    }

    /**
     * Keep the WHOLE sheet visible: inset the preview area so it ends above
     * the floating UI instead of running underneath. The panel now lives in
     * a popover (#materials-popover) stacked above the bottom dock
     * (#bottom-toolbar) - measure BOTH and inset past whichever reaches
     * highest under the overlay's right half.
     */
    fitPreviewAboveUI() {
        const overlayRect = this.overlay.getBoundingClientRect();
        let inset = 16;
        if (overlayRect.height > 0) {
            const blockers = [
                document.getElementById('bottom-toolbar'),
                document.getElementById('tab-bar')
            ];
            for (const el of blockers) {
                if (!el || el.offsetParent === null) continue; // absent/hidden
                const rect = el.getBoundingClientRect();
                if (rect.height <= 0) continue;
                // Only inset when the element actually reaches under the
                // overlay (right half of the window) horizontally
                const overlapsX = rect.right > overlayRect.left;
                const covered = overlayRect.bottom - rect.top;
                if (overlapsX && covered > 0) inset = Math.max(inset, covered + 12);
            }
        }
        this.previewDiv.style.bottom = `${inset}px`;
    }

    /**
     * While the split view is up, pan the camera so the model sits centred
     * in the free LEFT half instead of behind the pattern overlay.
     * @param {boolean} active
     */
    applySplitCameraShift(active) {
        const cam = this.app.cameraController;
        if (!cam) return;

        if (active && !this._splitShift) {
            // A quarter of the viewport width, in world units (1/zoom per px)
            const shift = (this.app.canvas?.clientWidth || window.innerWidth) / 4 / cam.zoom;
            cam.panOffset.x += shift;
            cam.updateCameraTransform();
            this._splitShift = shift;
        } else if (!active && this._splitShift) {
            cam.panOffset.x -= this._splitShift;
            cam.updateCameraTransform();
            this._splitShift = 0;
        }
    }

    /**
     * External hook (UIManager): force the split-screen preview off when
     * another tab takes over, or re-evaluate when this tab is shown
     */
    setSplitScreen(visible) {
        if (!visible) {
            this.overlay.style.display = 'none';
            // Undo the camera shift too (updateOverlayVisibility isn't on
            // this path - the panel may already be disconnected)
            this.applySplitCameraShift(false);
            // Leaving Make mode: also clear the flat-parts viewport preview
            // (animates the parts back into the assembled chair)
            this.updatePrintPreview();
        } else {
            this.updateOverlayVisibility();
        }
    }

    /**
     * Labelled number input; onChange fires with the parsed value
     */
    createNumberControl(labelText, value, unit, { min, max, step } = {}, onChange) {
        const row = document.createElement('div');
        row.style.cssText = 'display: flex; align-items: center; gap: 8px;';

        const label = document.createElement('div');
        label.textContent = labelText;
        label.style.cssText = 'color: #555; font-size: 12px; min-width: 120px;';
        row.appendChild(label);

        const input = document.createElement('input');
        input.type = 'number';
        input.value = value;
        if (min !== undefined) input.min = min;
        if (max !== undefined) input.max = max;
        if (step !== undefined) input.step = step;
        input.style.cssText = `
            background: #fff;
            border: 1px solid #ccc;
            border-radius: 6px;
            color: var(--text);
            padding: 5px 8px;
            width: 70px;
            font-size: 12px;
        `;
        input.addEventListener('change', () => {
            let v = parseFloat(input.value);
            if (!isFinite(v)) return;
            if (min !== undefined && v < min) { v = min; input.value = v; }
            onChange(v);
        });
        row.appendChild(input);

        const unitEl = document.createElement('div');
        unitEl.textContent = unit;
        unitEl.style.cssText = 'color: #888; font-size: 11px;';
        row.appendChild(unitEl);

        return row;
    }

    /**
     * Which SHEET_PRESETS index (as a string) matches the current
     * fabSettings width/height, or 'custom' if none does. Used to keep the
     * preset dropdown in sync with the number fields.
     * @returns {string}
     */
    matchSheetPreset() {
        const { sheetWidth, sheetHeight, cutScalePct } = this.app.fabSettings;
        const i = SHEET_PRESETS.findIndex(p =>
            p.w === sheetWidth && p.h === sheetHeight && p.scale === (cutScalePct ?? 100));
        return i >= 0 ? String(i) : 'custom';
    }

    /**
     * Labelled <select>; onChange fires with the chosen option's value.
     * Options carrying a `group` field are bundled under <optgroup> headings
     * (in first-seen order); ungrouped options render at the top level.
     * @param {string} labelText
     * @param {Array<{label: string, value: string, group?: string}>} options
     * @param {string} value - initially selected option value
     * @param {(value: string) => void} onChange
     */
    createSelectControl(labelText, options, value, onChange) {
        const row = document.createElement('div');
        row.style.cssText = 'display: flex; align-items: center; gap: 8px;';

        const label = document.createElement('div');
        label.textContent = labelText;
        label.style.cssText = 'color: #555; font-size: 12px; min-width: 120px;';
        row.appendChild(label);

        const select = document.createElement('select');
        select.style.cssText = `
            background: #fff;
            border: 1px solid #ccc;
            border-radius: 6px;
            color: var(--text);
            padding: 5px 8px;
            font-size: 12px;
            cursor: pointer;
        `;
        const makeOption = (opt) => {
            const o = document.createElement('option');
            o.value = opt.value;
            o.textContent = opt.label;
            return o;
        };
        const groups = new Map(); // group name -> <optgroup>
        for (const opt of options) {
            if (!opt.group) {
                select.appendChild(makeOption(opt));
                continue;
            }
            let og = groups.get(opt.group);
            if (!og) {
                og = document.createElement('optgroup');
                og.label = opt.group;
                groups.set(opt.group, og);
                select.appendChild(og);
            }
            og.appendChild(makeOption(opt));
        }
        select.value = value;
        select.addEventListener('change', () => onChange(select.value));
        row.appendChild(select);

        return row;
    }

    /**
     * Simple action button
     */
    createActionButton(label, tooltip, action) {
        const button = document.createElement('button');
        button.textContent = label;
        button.title = tooltip;
        button.className = 'sc-tool-btn';
        button.style.cssText = `
            background: var(--accent);
            border: none;
            border-radius: 6px;
            color: #fff;
            padding: 8px 20px;
            cursor: pointer;
            font-size: 13px;
            font-weight: 600;
        `;
        button.addEventListener('mouseenter', () => {
            button.style.background = 'var(--accent-strong)';
        });
        button.addEventListener('mouseleave', () => {
            button.style.background = 'var(--accent)';
        });
        button.addEventListener('click', action);
        return button;
    }

    // =========================================================================
    // Preview refresh
    // =========================================================================

    /**
     * Debounced refresh - called from Application's onShapeChange callback
     * on every design change (safe to call at any frequency)
     */
    refreshPattern() {
        clearTimeout(this._refreshTimer);
        this._refreshTimer = setTimeout(() => this.refreshNow(), REFRESH_DEBOUNCE_MS);
    }

    /**
     * Immediate refresh of the pattern preview and model dimensions.
     * Skipped while the Fabrication tab is hidden (UIManager calls
     * update() when the tab is shown, which lands back here).
     */
    refreshNow() {
        clearTimeout(this._refreshTimer);
        // Keep the viewport flat-parts preview in sync (enters/leaves/
        // refreshes itself based on the current mode; idempotent)
        this.updatePrintPreview();
        if (!this.panel || !this.panel.isConnected) {
            // Panel not visible (Design mode): make sure the split screen
            // is gone and the camera shift is undone too
            this.overlay.style.display = 'none';
            this.applySplitCameraShift(false);
            return;
        }

        this.updateDimensions();

        // Split-screen pattern preview only while the Cut sub-tab is up
        if (!this.updateOverlayVisibility()) return;

        try {
            const parts = collectParts(
                this.app.basePlanes || [],
                this.app.basePlaneSketchStates || [],
                this.app.slicePlaneManager?.planes || [],
                { scale: (this.app.fabSettings.cutScalePct ?? 100) / 100 }
            );

            if (parts.length === 0) {
                this.previewDiv.innerHTML =
                    '<div style="color:#999; font-size:12px; padding:16px; text-align:center;">' +
                    'Draw a chair to see the cut pattern</div>';
                this.previewInfo.textContent = '';
                this.pageLabel.textContent = 'Page 1/1';
                this.prevPageBtn.disabled = true;
                this.nextPageBtn.disabled = true;
                return;
            }

            const result = buildSVG(parts, { ...this.getPackOpts(), page: this.previewPage });
            // buildSVG clamps the page index (e.g. after the page count
            // shrank); adopt the page actually rendered
            this.previewPage = result.page;

            this.previewDiv.innerHTML = result.svg;

            // Scale the SVG to fit the half-screen preview, aspect
            // preserved (the viewBox is authoritative; drop the mm size)
            const svgEl = this.previewDiv.querySelector('svg');
            if (svgEl) {
                svgEl.setAttribute('width', '100%');
                svgEl.setAttribute('height', '100%');
                svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
                svgEl.style.display = 'block';

                // Show the sheet as a page: set the viewBox to the full
                // configured sheet and draw its boundary
                const opts = this.getPackOpts();
                const sw = opts.sheetWidth || 1219;
                const sh = opts.sheetHeight || 2438;
                svgEl.setAttribute('viewBox', `-2 -2 ${sw + 4} ${sh + 4}`);
                const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                rect.setAttribute('x', '0'); rect.setAttribute('y', '0');
                rect.setAttribute('width', String(sw)); rect.setAttribute('height', String(sh));
                rect.setAttribute('fill', 'none');
                rect.setAttribute('stroke', '#999');
                rect.setAttribute('stroke-width', '1');
                rect.setAttribute('stroke-dasharray', '6 4');
                svgEl.insertBefore(rect, svgEl.firstChild);
            }

            this.pageLabel.textContent = `Page ${result.page + 1}/${result.pageCount}`;
            this.prevPageBtn.disabled = result.page <= 0;
            this.nextPageBtn.disabled = result.page >= result.pageCount - 1;
            this.prevPageBtn.style.opacity = this.prevPageBtn.disabled ? '0.3' : '1';
            this.nextPageBtn.style.opacity = this.nextPageBtn.disabled ? '0.3' : '1';

            this.previewInfo.textContent =
                `${result.partCount} part${result.partCount === 1 ? '' : 's'} on page - ` +
                `sheet ${result.widthMM} x ${result.heightMM} mm`;
        } catch (error) {
            console.warn('Cut pattern preview failed:', error);
            this.previewDiv.innerHTML =
                '<div style="color:#c00; font-size:12px; padding:16px;">Preview failed</div>';
            this.previewInfo.textContent = '';
        }
    }

    /**
     * Viewport preview for the 'flat' print mode: when Unassembled (flat
     * parts) is selected on the 3D Print tab, hide the assembled chair and
     * ANIMATE each part from its assembled pose in the chair down to its
     * packed flat spot on the ground plane (and animate back when the mode
     * is left). Cleared in any other state.
     */
    updatePrintPreview() {
        const active = this.printMode === 'flat' &&
            this.activeSubTab === 'print' &&
            !!(this.panel && this.panel.isConnected);

        if (active) {
            this.enterFlatPreview();
        } else {
            this.leaveFlatPreview();
        }
    }

    /**
     * A part's assembled pose IN THE CHAIR AS IT CURRENTLY SITS.
     *
     * computeAssembledPose builds the pose from the part's SlicePlane, so it is
     * in the chair's GENERATED frame. The chair on screen is not necessarily
     * there: the hand tool parks it (and the simulation can displace it), and
     * syncMeshes renders the chair meshes through that transform. Spawning the
     * flat parts at the raw assembled pose therefore made them appear at the
     * chair's design position rather than where the chair actually is - so
     * entering Unassembled snapped the model across, and leaving it re-assembled
     * the parts at the design pose before the real meshes blinked back at the
     * dragged one.
     *
     * Push the pose through the same delta the chair meshes and the sketch
     * overlay use, and the parts spawn and land exactly on the chair.
     *
     * @param {THREE.Mesh} mesh - Flat part mesh (userData.assembledPose/flatPose)
     * @returns {{position: THREE.Vector3, quaternion: THREE.Quaternion}}
     */
    assembledPoseOf(mesh) {
        const pose = mesh.userData.assembledPose || mesh.userData.flatPose;
        const delta = this.app.getChairFrameDelta?.() ?? null;
        if (!delta) return pose;

        return {
            position: pose.position.clone()
                .applyQuaternion(delta.quaternion)
                .add(delta.position),
            quaternion: delta.quaternion.clone().multiply(pose.quaternion)
        };
    }

    /**
     * Enter (or refresh) the flat-parts preview. Spawns the part meshes at
     * their assembled poses and tweens them to their packed flat poses.
     * Re-entrant: if the exit animation is running it retargets back to
     * flat from wherever the parts currently are; if the preview is
     * already settled it rebuilds statically (the design may have changed).
     */
    enterFlatPreview() {
        // Remember the user's rotation so leaving the preview can swing
        // back to it. The camera itself is ANIMATED to the top-down
        // print-bed view (FLAT_VIEW_ROTATION) by startFlatAnim, in sync
        // with the parts unfolding.
        const rot = this.app.sceneRotation;
        if (rot && !this._preFlatRotation) {
            this._preFlatRotation = { x: rot.rotationX ?? 0, y: rot.rotationY ?? 0 };
        }

        if (this.flatPreviewGroup) {
            if (this._flatAnim) {
                // Mid-animation: retarget the exit animation back to flat;
                // an already-running enter animation just keeps going
                if (this._flatAnim.direction === 'out') this.retargetFlatAnim('in');
            } else {
                // Settled in flat mode: rebuild in place (no re-animation)
                this.rebuildFlatPreviewStatic();
            }
            return;
        }

        const { meshes } = this.buildFlatMeshes();
        if (!meshes || meshes.length === 0) return;

        // Poses are expressed in the rotation-pivot frame (the same frame
        // the generated meshes' local transforms live in); an identity
        // group added via sceneManager keeps add/remove trivial
        const group = new THREE.Group();
        group.name = 'FlatPartsPreview';
        const items = meshes.map((mesh, i) => {
            const flat = mesh.userData.flatPose;
            // Spawn ON the chair wherever it currently sits - not at its
            // generated pose (see assembledPoseOf)
            const assembled = this.assembledPoseOf(mesh);
            mesh.scale.setScalar(flat.scale);
            mesh.position.copy(assembled.position);
            mesh.quaternion.copy(assembled.quaternion);
            group.add(mesh);
            return {
                mesh,
                fromPos: assembled.position.clone(),
                fromQuat: assembled.quaternion.clone(),
                toPos: flat.position.clone(),
                toQuat: flat.quaternion.clone(),
                delay: i * FLAT_STAGGER_MS
            };
        });

        this.app.generatedMeshes.forEach(m => { m.visible = false; });
        this.app.sceneManager.add(group);
        this.flatPreviewGroup = group;
        this.widenShadowToFlatLayout(meshes);
        this.startFlatAnim(items, 'in');
    }

    /**
     * Grow the sun's shadow frustum to enclose the flat-packed layout so
     * every part receives shadow (and thus reads at the same brightness).
     * The parts spread across the ground far wider than the assembled
     * chair, and the default frustum only covers the assembled footprint -
     * parts landing outside it get no shadow and look lighter.
     *
     * @param {THREE.Mesh[]} meshes - Flat part meshes (userData.flatPose set)
     */
    widenShadowToFlatLayout(meshes) {
        const sm = this.app.sceneManager;
        if (!sm?.setSunShadowExtent) return;

        let half = 0;
        for (const mesh of meshes) {
            const flat = mesh.userData.flatPose;
            if (!flat) continue;
            mesh.geometry.computeBoundingSphere();
            const r = (mesh.geometry.boundingSphere?.radius || 0) * flat.scale;
            // Layout lies in the ground (XZ) plane, centred on the origin
            half = Math.max(half, Math.abs(flat.position.x) + r, Math.abs(flat.position.z) + r);
        }
        sm.setSunShadowExtent(half);
    }

    /**
     * Leave the flat-parts preview: tween the parts back from wherever
     * they currently are to their assembled poses, then remove them and
     * re-show the generated chair meshes. Re-entrant: retargets a running
     * enter animation; a no-op while already exiting or already cleared.
     */
    leaveFlatPreview() {
        if (!this.flatPreviewGroup) {
            // Nothing to animate back from: restore the stored rotation
            // instantly (e.g. the preview never actually built any parts)
            if (this._preFlatRotation && this.app.sceneRotation) {
                this.app.sceneRotation.setRotation(this._preFlatRotation.x, this._preFlatRotation.y);
            }
            this._preFlatRotation = null;
            this.app.generatedMeshes.forEach(m => { m.visible = true; });
            return;
        }
        if (this._flatAnim && this._flatAnim.direction === 'out') return;

        // The exit animation swings the camera back to _preFlatRotation
        // alongside the parts re-assembling (cleared in disposeFlatPreview)
        this.retargetFlatAnim('out');
    }

    /**
     * (Re)start the tween of every preview part from its CURRENT transform
     * to the given end ('in' = flat pose, 'out' = assembled pose). Used
     * both to reverse a running animation cleanly and to start the exit.
     * @param {'in'|'out'} direction
     */
    retargetFlatAnim(direction) {
        if (!this.flatPreviewGroup) return;
        const items = this.flatPreviewGroup.children.map((mesh, i) => {
            // Re-assemble ONTO the chair as it currently sits, so the parts
            // land exactly where the real meshes reappear (see assembledPoseOf)
            const target = direction === 'in'
                ? mesh.userData.flatPose
                : this.assembledPoseOf(mesh);
            return {
                mesh,
                fromPos: mesh.position.clone(),
                fromQuat: mesh.quaternion.clone(),
                toPos: target.position.clone(),
                toQuat: target.quaternion.clone(),
                delay: i * FLAT_STAGGER_MS
            };
        });
        if (items.length === 0) {
            this.disposeFlatPreview();
            return;
        }
        this.startFlatAnim(items, direction);
    }

    /**
     * Drive the position (lerp) + quaternion (slerp) tween with an
     * easeInOutCubic profile and a small per-part stagger. Replaces any
     * running animation (single RAF loop - mode flips retarget, never
     * stack). When an 'out' run completes the preview is torn down and the
     * chair re-shown.
     * @param {Array} items - {mesh, fromPos, fromQuat, toPos, toQuat, delay}
     * @param {'in'|'out'} direction
     */
    startFlatAnim(items, direction) {
        if (this._flatAnim) cancelAnimationFrame(this._flatAnim.raf);
        const anim = { items, direction, start: performance.now(), raf: 0 };

        // Camera tween: swing the scene rotation to the top-down print-bed
        // view while the parts unfold (and back to the stored pre-flat
        // rotation on exit), over the same FLAT_ANIM_MS/easeInOutCubic the
        // parts use - one continuous move. Cancelled mid-flight if the
        // user grabs the rotation (external change detected per frame).
        const rot = this.app.sceneRotation;
        const camTarget = direction === 'in' ? FLAT_VIEW_ROTATION : this._preFlatRotation;
        if (rot && camTarget) {
            const fromX = rot.rotationX ?? 0;
            const fromY = rot.rotationY ?? 0;
            const TWO_PI = Math.PI * 2;
            // Shortest angular path for the Y spin
            const dY = ((camTarget.y - fromY + Math.PI) % TWO_PI + TWO_PI) % TWO_PI - Math.PI;
            anim.cam = { fromX, fromY, toX: camTarget.x, dY, lastX: fromX, lastY: fromY };
        }
        this._flatAnim = anim;

        const tick = (now) => {
            if (this._flatAnim !== anim) return; // superseded
            let done = true;
            for (const it of anim.items) {
                const t = Math.min(Math.max(
                    (now - anim.start - it.delay) / FLAT_ANIM_MS, 0), 1);
                if (t < 1) done = false;
                const e = easeInOutCubic(t);
                it.mesh.position.lerpVectors(it.fromPos, it.toPos, e);
                it.mesh.quaternion.slerpQuaternions(it.fromQuat, it.toQuat, e);
            }

            if (anim.cam) {
                const cam = anim.cam;
                if (Math.abs(rot.rotationX - cam.lastX) > 1e-4 ||
                    Math.abs(rot.rotationY - cam.lastY) > 1e-4) {
                    // The user rotated the view mid-flight - let go of the
                    // camera (the parts keep animating)
                    anim.cam = null;
                } else {
                    const t = Math.min(Math.max((now - anim.start) / FLAT_ANIM_MS, 0), 1);
                    if (t < 1) done = false;
                    const e = easeInOutCubic(t);
                    rot.setRotation(
                        cam.fromX + (cam.toX - cam.fromX) * e,
                        cam.fromY + cam.dY * e
                    );
                    // Track the values the controller actually applied
                    // (setRotation clamps X) for the user-grab detection
                    cam.lastX = rot.rotationX;
                    cam.lastY = rot.rotationY;
                    if (t >= 1) anim.cam = null;
                }
            }

            if (!done) {
                anim.raf = requestAnimationFrame(tick);
            } else {
                this._flatAnim = null;
                if (anim.direction === 'out') this.disposeFlatPreview();
            }
        };
        anim.raf = requestAnimationFrame(tick);
    }

    /**
     * Rebuild the settled flat preview from the current design without
     * animating (refresh while the flat mode is already active)
     */
    rebuildFlatPreviewStatic() {
        this.removeFlatGroup();

        const { meshes } = this.buildFlatMeshes();
        if (!meshes || meshes.length === 0) {
            this.app.generatedMeshes.forEach(m => { m.visible = true; });
            return;
        }

        const group = new THREE.Group();
        group.name = 'FlatPartsPreview';
        for (const mesh of meshes) {
            const flat = mesh.userData.flatPose;
            mesh.scale.setScalar(flat.scale);
            mesh.position.copy(flat.position);
            mesh.quaternion.copy(flat.quaternion);
            group.add(mesh);
        }

        this.app.generatedMeshes.forEach(m => { m.visible = false; });
        this.app.sceneManager.add(group);
        this.flatPreviewGroup = group;
        this.widenShadowToFlatLayout(meshes);
    }

    /**
     * Remove and dispose the preview group and stop any running animation
     * (does NOT touch the generated meshes' visibility)
     */
    removeFlatGroup() {
        if (this._flatAnim) {
            cancelAnimationFrame(this._flatAnim.raf);
            this._flatAnim = null;
        }
        if (!this.flatPreviewGroup) return;
        this.flatPreviewGroup.parent?.remove(this.flatPreviewGroup);
        this.flatPreviewGroup.traverse(o => {
            if (o.geometry) o.geometry.dispose();
            if (o.material) o.material.dispose();
        });
        this.flatPreviewGroup = null;
    }

    /**
     * Tear the preview down completely and re-show the assembled chair.
     * The exit camera tween has already returned the rotation to the
     * stored pre-flat pose (or the user grabbed the camera - either way
     * the stored rotation is done with).
     */
    disposeFlatPreview() {
        this.removeFlatGroup();
        this._preFlatRotation = null;
        this.app.sceneManager?.setSunShadowExtent?.(null); // back to crisp assembled-chair shadows
        this.app.generatedMeshes.forEach(m => { m.visible = true; });
    }

    /**
     * Build the flat-packed part meshes for the 'flat' print mode (parts
     * with slot cuts, packed like the 2D pattern, extruded to their
     * plane's thickness, in mm). Each mesh carries its baked flat mm
     * transform (STL export path) plus userData.flatPose/assembledPose in
     * world units for the viewport animation.
     */
    buildFlatMeshes() {
        const parts = collectParts(
            this.app.basePlanes || [],
            this.app.basePlaneSketchStates || [],
            this.app.slicePlaneManager?.planes || []
        );
        return buildFlatPartMeshes(parts, {
            // No sheetWidth: these parts go on a printer bed, not a cut sheet,
            // so let buildFlatPartMeshes pack them into a square-ish blob
            // instead of a long strip pinned to the material width
            gap: this.app.fabSettings.gap,
            defaultThickness: this.app.getEffectiveThickness?.() ?? this.app.materialThickness,
            groundY: this.app.physicsWorld?.groundY ?? -5
        });
    }

    /**
     * Update the 3D-print dimensions readout (mm at the current scale),
     * reflecting the selected print mode: assembled modes measure the
     * generated meshes; flat mode measures the packed flat layout.
     */
    updateDimensions() {
        if (!this.dimsInfo) return;

        let dims = null;
        let prefix = 'Model';
        try {
            if (this.printMode === 'flat') {
                prefix = 'Layout';
                const { meshes } = this.buildFlatMeshes();
                // Flat meshes are already in mm; only the user scale applies
                dims = computeMeshesBBox(meshes, this.scalePct / 100);
            } else {
                // 'assembled' and 'noslots' have the same overall bbox
                // (slot cutouts are interior)
                dims = computeMeshesBBox(this.app.generatedMeshes || [], 100 * (this.scalePct / 100));
            }
        } catch (error) {
            console.warn('updateDimensions failed:', error);
        }

        this.dimsInfo.textContent = dims
            ? `${prefix}: ${dims.widthMM.toFixed(0)} x ${dims.heightMM.toFixed(0)} x ${dims.depthMM.toFixed(0)} mm`
            : `${prefix}: no geometry yet`;
    }

    // =========================================================================
    // STL export
    // =========================================================================

    /**
     * Export a binary STL (mm) according to the selected print mode:
     * - assembled: the generated meshes as-is (slot cutouts included)
     * - noslots:   the assembled geometry regenerated without slot cutouts
     * - flat:      parts (with slot cuts) packed flat on the ground plane
     */
    exportSTL() {
        let buffer;
        let filename = 'chair.stl';

        if (this.printMode === 'flat') {
            const { meshes } = this.buildFlatMeshes();
            if (meshes.length === 0) {
                console.warn('exportSTL: nothing to export');
                return;
            }
            // Flat meshes are already in mm; only the user scale applies
            buffer = buildSTL(meshes, this.scalePct / 100);
            filename = 'chair-flat-parts.stl';
        } else if (this.printMode === 'noslots') {
            buffer = this.buildNoSlotsSTL();
            if (!buffer) return;
            filename = 'chair-no-slots.stl';
        } else {
            const meshes = this.app.generatedMeshes || [];
            if (meshes.length === 0) {
                console.warn('exportSTL: nothing to export');
                return;
            }
            buffer = buildSTL(meshes, 100 * (this.scalePct / 100));
        }

        console.log(`STL export (${this.printMode}): ${(buffer.byteLength - 84) / 50} triangles at ${this.scalePct}%`);

        const blob = new Blob([buffer], { type: 'model/stl' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    }

    /**
     * Build the assembled-without-slots STL: temporarily flag the app to
     * skip slot cutouts, run the (synchronous) mesh regeneration, snapshot
     * the STL, then restore the real geometry.
     * @returns {ArrayBuffer|null}
     */
    buildNoSlotsSTL() {
        const app = this.app;
        let buffer = null;

        app.exportWithoutSlots = true;
        try {
            // DELIBERATE exception to the canonical pipeline
            // (Application.regenerateAll): this is a TEMPORARY-flag mesh
            // refresh for export, not a parametric mutation, and it must
            // run synchronously so the STL is built from the flag state.
            app.regenerateMeshesFromCurrentSketch();
            app.generateMeshesForAllSlicePlanes();
            if ((app.generatedMeshes || []).length > 0) {
                buffer = buildSTL(app.generatedMeshes, 100 * (this.scalePct / 100));
            } else {
                console.warn('exportSTL: nothing to export');
            }
        } finally {
            // Always restore the real (slotted) geometry
            app.exportWithoutSlots = false;
            app.regenerateMeshesFromCurrentSketch();
            app.generateMeshesForAllSlicePlanes();
        }

        return buffer;
    }

    /**
     * Get the panel element
     */
    getElement() {
        return this.panel;
    }

    /**
     * Update panel state (called by UIManager when the tab is shown)
     */
    update() {
        this.refreshNow();
    }

    /**
     * Cleanup (the overlay lives outside the panel element)
     */
    dispose() {
        clearTimeout(this._refreshTimer);
        this.disposeFlatPreview();
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.parentNode.removeChild(this.overlay);
        }
    }
}
